import ExcelJS from 'exceljs';

const ROLE_GROUPS = [
  {
    key: 'HOUSEKEEPING',
    label: 'HOUSEKEEPING PAYROLL',
    roles: ['HOUSEKEEPER', 'HOUSEKEEPER_MANAGER'],
  },
  {
    key: 'INSPECTOR',
    label: 'INSPECTOR PAYROLL',
    roles: ['INSPECTOR'],
  },
  {
    key: 'PROPERTY_MANAGER',
    label: 'PROPERTY MANAGER PAYROLL',
    roles: ['PROPERTY_MANAGER'],
  },
  {
    key: 'MAINTENANCE',
    label: 'MAINTENANCE PAYROLL',
    roles: ['MAINTENANCE'],
  },
  {
    key: 'CALL_CENTER',
    label: 'CALL CENTER PAYROLL',
    roles: ['CALL_CENTER'],
  },
  {
    key: 'ADMIN',
    label: 'ADMIN PAYROLL',
    roles: ['ADMIN'],
  },
  {
    key: 'GUEST_APP_MANAGER',
    label: 'GUEST APP MANAGER PAYROLL',
    roles: ['GUEST_APP_MANAGER'],
  },
  {
    key: 'OTHER',
    label: 'OTHER PAYROLL',
    roles: ['USER'],
  },
];

const AREA_LABELS = {
  KIAWAH: 'Kiawah',
  ISLE_OF_PALMS: 'Isle of Palms',
  ISLE_OF_PALMS_WILD_DUNES: 'Isle of Palms - Wild Dunes',
  SEABROOK_ISLAND: 'Seabrook Island',
};

const ROLE_TO_GROUP = ROLE_GROUPS.reduce((map, group) => {
  group.roles.forEach((role) => {
    map.set(role, group.key);
  });
  return map;
}, new Map());

const normalizeAreas = (areas) => (Array.isArray(areas) ? areas.filter(Boolean) : []);

const areaLabel = (area) => AREA_LABELS[area] || String(area || '').replace(/_/g, ' ');

const primaryAreaKey = (employeeRow) => normalizeAreas(employeeRow.areas)[0] || 'UNASSIGNED';

const primaryAreaLabel = (employeeRow) => {
  const area = primaryAreaKey(employeeRow);
  return area === 'UNASSIGNED' ? 'Unassigned Area' : areaLabel(area);
};

const moneyValue = (value) => {
  const numericValue = Number(value || 0);
  if (Number.isNaN(numericValue)) return 0;
  return Number(numericValue.toFixed(2));
};

const formatDateSegment = (value) => {
  if (!value) return 'date';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatDateRangeLabel = (startDate, endDate) => {
  const startLabel = formatDateLabel(startDate);
  const endLabel = formatDateLabel(endDate);
  if (startLabel && endLabel) return `Date Range: ${startLabel} - ${endLabel}`;
  return 'Date Range';
};

const applyCellBorder = (cell) => {
  cell.border = {
    top: { style: 'thin', color: { argb: 'FF000000' } },
    left: { style: 'thin', color: { argb: 'FF000000' } },
    bottom: { style: 'thin', color: { argb: 'FF000000' } },
    right: { style: 'thin', color: { argb: 'FF000000' } },
  };
};

const styleDateRangeRow = (worksheet, row) => {
  worksheet.mergeCells(row.number, 1, row.number, 3);
  row.height = 22;
  const cell = row.getCell(1);
  applyCellBorder(cell);
  cell.font = {
    name: 'Arial',
    bold: true,
    size: 12,
    color: { argb: 'FF000000' },
  };
  cell.alignment = {
    horizontal: 'center',
    vertical: 'middle',
  };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFFFFF' },
  };
};

const styleSectionRow = (worksheet, row, showNotesHeader = false) => {
  worksheet.mergeCells(row.number, 1, row.number, 2);
  row.height = 21;
  row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
    applyCellBorder(cell);
    cell.font = {
      name: 'Arial',
      bold: true,
      size: 14,
      color: { argb: 'FF000000' },
    };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      shrinkToFit: true,
    };

    if (columnNumber <= 2) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF00' },
      };
    } else if (showNotesHeader) {
      cell.value = 'NOTES';
    }
  });
};

const styleAreaRow = (worksheet, row) => {
  worksheet.mergeCells(row.number, 1, row.number, 3);
  row.height = 20;
  const cell = row.getCell(1);
  applyCellBorder(cell);
  cell.font = {
    name: 'Arial',
    bold: true,
    size: 11,
    color: { argb: 'FFFFFFFF' },
  };
  cell.alignment = {
    horizontal: 'center',
    vertical: 'middle',
    shrinkToFit: true,
  };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1F2937' },
  };
};

const styleEmployeeRow = (row, isReviewRow = false) => {
  row.height = 21;
  row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
    applyCellBorder(cell);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF2F2F2' },
    };
    cell.font = {
      name: 'Arial',
      bold: true,
      size: 13,
      color: { argb: isReviewRow ? 'FFFF0000' : 'FF000000' },
    };
    cell.alignment = {
      horizontal: columnNumber === 3 ? 'left' : 'center',
      vertical: 'middle',
      wrapText: true,
      shrinkToFit: columnNumber !== 3,
    };
  });
  row.getCell(2).numFmt = '$#,##0.00';
};

const styleBlankRow = (row) => {
  row.height = 18;
  for (let column = 1; column <= 3; column += 1) {
    const cell = row.getCell(column);
    applyCellBorder(cell);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF2F2F2' },
    };
  }
};

const downloadExcel = (buffer, filename) => {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const createRoleGroupMap = () => (
  ROLE_GROUPS.reduce((map, group) => {
    map.set(group.key, []);
    return map;
  }, new Map())
);

const groupRowsByAreaAndRole = (rows) => {
  const groupedByArea = new Map();

  rows.forEach((row) => {
    const areaKey = primaryAreaKey(row);
    if (!groupedByArea.has(areaKey)) {
      groupedByArea.set(areaKey, {
        key: areaKey,
        label: primaryAreaLabel(row),
        roleGroups: createRoleGroupMap(),
      });
    }

    const areaGroup = groupedByArea.get(areaKey);
    const roleGroupKey = ROLE_TO_GROUP.get(row.role) || 'OTHER';
    areaGroup.roleGroups.get(roleGroupKey).push(row);
  });

  return Array.from(groupedByArea.values())
    .map((areaGroup) => {
      areaGroup.roleGroups.forEach((groupRows) => {
        groupRows.sort((a, b) => a.userName.localeCompare(b.userName));
      });
      return areaGroup;
    })
    .sort((a, b) => {
      if (a.key === 'UNASSIGNED') return 1;
      if (b.key === 'UNASSIGNED') return -1;
      return a.label.localeCompare(b.label);
    });
};

const shouldHighlightEmployeeRow = (employeeRow) => (
  Boolean(employeeRow.isReviewRow) || Number(employeeRow.totalMinutes || 0) <= 0
);

export const exportHourlyPayrollToExcel = async ({ employeeRows, startDate, endDate }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sweetgrass Properties';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Hourly Payroll', {
    views: [{ showGridLines: false }],
  });

  worksheet.columns = [
    { width: 34 },
    { width: 20 },
    { width: 36 },
  ];

  const groupedRows = groupRowsByAreaAndRole(employeeRows || []);
  let hasWrittenSection = false;

  const dateRangeRow = worksheet.addRow([formatDateRangeLabel(startDate, endDate), '', '']);
  styleDateRangeRow(worksheet, dateRangeRow);

  groupedRows.forEach((areaGroup) => {
    const hasRowsForArea = ROLE_GROUPS.some((group) => (areaGroup.roleGroups.get(group.key) || []).length > 0);
    if (!hasRowsForArea) return;

    if (hasWrittenSection) {
      styleBlankRow(worksheet.addRow(['', '', '']));
    }

    const areaRow = worksheet.addRow([`${areaGroup.label.toUpperCase()} AREA`, '', '']);
    styleAreaRow(worksheet, areaRow);

    ROLE_GROUPS.forEach((group) => {
      const rows = areaGroup.roleGroups.get(group.key) || [];
      if (rows.length === 0) return;

      const sectionRow = worksheet.addRow([group.label, '', hasWrittenSection ? '' : 'NOTES']);
      styleSectionRow(worksheet, sectionRow, !hasWrittenSection);

      rows.forEach((employeeRow) => {
        const row = worksheet.addRow([
          employeeRow.userName,
          moneyValue(employeeRow.totalCost),
          '',
        ]);
        styleEmployeeRow(row, shouldHighlightEmployeeRow(employeeRow));
      });

      hasWrittenSection = true;
    });
  });

  if (!hasWrittenSection) {
    const sectionRow = worksheet.addRow(['HOURLY PAYROLL', '', 'NOTES']);
    styleSectionRow(worksheet, sectionRow, true);
    styleEmployeeRow(worksheet.addRow(['No payroll rows found', 0, '']));
  }

  worksheet.pageSetup = {
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    orientation: 'portrait',
    margins: {
      left: 0.25,
      right: 0.25,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3,
    },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const filename = `hourly-payroll-${formatDateSegment(startDate)}-to-${formatDateSegment(endDate)}.xlsx`;
  downloadExcel(buffer, filename);
};

export default exportHourlyPayrollToExcel;
